/**
 * The 7 canonical values the `cars.listing_status` column may hold. This is
 * the single source of truth for both the API server and frontend so that
 * status string literals don't drift between code paths.
 *
 *  - `incomplete`        – host is still filling in the required fields
 *  - `ready_to_submit`   – submission requirements pass; still offline, host can submit for review
 *  - `awaiting_approval` – submitted, queued for moderator/auto review
 *  - `active`            – approved and publicly listed
 *  - `inactive`          – paused (admin or owner) but not deleted/rejected
 *  - `rejected`          – moderator rejected the listing
 *  - `deleted`           – soft-deleted by owner or admin
 */
export const LISTING_STATUSES = [
  "incomplete",
  "ready_to_submit",
  "awaiting_approval",
  "active",
  "inactive",
  "rejected",
  "deleted",
] as const;

export type ListingStatus = typeof LISTING_STATUSES[number];

/** Canonical availability labels; moderation tasks are a separate dimension. */
export const LISTING_STATUS_LABELS: Record<ListingStatus, string> = {
  incomplete: "Draft",
  ready_to_submit: "Ready to submit",
  awaiting_approval: "Pending review",
  active: "Live",
  inactive: "Paused",
  rejected: "Rejected",
  deleted: "Removed",
};

export function isListingStatus(value: unknown): value is ListingStatus {
  return typeof value === "string" && (LISTING_STATUSES as readonly string[]).includes(value);
}
